import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, X, Send } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useToast } from "@/contexts/ToastContext";

interface ChangeReferralResponseModalProps {
  referral: {
    id: number;
    clientName?: string;
    senderName?: string;
  };
  isOpen: boolean;
  onClose: () => void;
}

export default function ChangeReferralResponseModal({
  referral,
  isOpen,
  onClose,
}: ChangeReferralResponseModalProps) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const changeResponseMutation = useMutation({
    mutationFn: async () => {
      return api.put(`/api/workflows/referral/${referral.id}/status`, {
        status: "declined",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/referral/list"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["referrals"] });
      showSuccess(
        "Response Changed",
        referral.senderName
          ? `${referral.senderName} has been notified that you declined this referral.`
          : "The sender has been notified that you declined this referral."
      );
      onClose();
    },
    onError: (error: any) => {
      showError(
        "Couldn't Change Response",
        error?.message || "Failed to change your response. Please try again."
      );
    },
  });

  const client = referral.clientName || "this client";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md bg-white/95 backdrop-blur-lg border-0 shadow-xl">
        <DialogHeader className="border-b border-amber-100 pb-4 bg-gradient-to-r from-amber-50 to-orange-50 -m-6 mb-0 p-6 rounded-t-lg">
          <DialogTitle className="flex items-center gap-2 text-gray-900 tracking-tight font-bold text-xl">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
            Change Your Response?
          </DialogTitle>
          <DialogDescription className="text-gray-700 mt-1">
            Decline the referral for{" "}
            <span className="font-semibold text-amber-900">{client}</span>
            {referral.senderName && (
              <>
                {" "}
                from <span className="font-semibold text-amber-900">{referral.senderName}</span>
              </>
            )}
            ?
          </DialogDescription>
        </DialogHeader>

        <div className="py-5 space-y-3 text-sm text-gray-700">
          <p>
            This will change your response from <span className="font-semibold">Accepted</span> to{" "}
            <span className="font-semibold text-red-600">Declined</span>.
          </p>
          <ul className="list-disc list-inside space-y-1 text-gray-600">
            <li>
              {referral.senderName ? referral.senderName : "The sender"} will be notified that you
              declined.
            </li>
            <li>You will no longer be able to close this referral out.</li>
            <li>
              The sender may forward this referral to another partner.
            </li>
          </ul>
        </div>

        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={changeResponseMutation.isPending}
            className="flex-1 flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" />
            Keep Accepted
          </Button>
          <Button
            onClick={() => changeResponseMutation.mutate()}
            disabled={changeResponseMutation.isPending}
            className="flex-1 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-medium"
          >
            {changeResponseMutation.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Changing...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Decline Referral
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
